import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import Typography from '@material-ui/core/Typography';
import { CircularProgress } from '@material-ui/core';

import CalcIcon from 'components/Icons/CalcIcon';
import compose from 'helpers/compose';
import Switch from 'components/FormFields/Switch';
import FormTextField from 'components/FormFields/FormTextField';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormDateSelects from 'components/FormFields/FormDateSelects';
import DatePicker from 'components/FormFields/FormDatePicker';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import { YearAndMonth } from 'common/interfaces';
import { validationErrToForm, f2p, getYearsAndMonthsFromLuxon } from 'helpers/helpers';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import ConnectIcon from 'components/Icons/ConnectIcon';
import { DepositFrequency } from 'stores/workflowStores/finances/depositsStore/model';
import Info from 'components/Tooltip/Info';

import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import {
  ExtProductAsset,
  ExternalProductData,
  ExternalProductOptions,
  ExtProductType,
  ExtProductFreq,
  ExtProductCategory,
} from './model';
import { Currency, EdwardColors } from 'enums';
import FinInstSearch from './FinInstSearch';
import ExternalProductModalComposition from './ExternalProductModalComposition';

interface OwnProps {
  currency: Currency;
  productId?: string;
  options: ExternalProductOptions;
  formikProps: FormikProps<ExternalProductData>;
  updateFinInst: () => void;
  maxDate?: YearAndMonth;
  setModel: (model: boolean) => void;
  nominal: boolean;
  setNominal: (nominal: boolean) => void;
}

interface Stores {
  extProductsStore: ExternalProductsStore;
}

type Props = OwnProps & Stores;

// TODO: move to helper
export const valueFilledIn = (input: any) => {
  return input && input !== 0 && input !== '0' && input !== '';
};

const ExternalProductModalSetup: React.FC<Props> = ({
  productId,
  extProductsStore,
  currency,
  options,
  formikProps,
  updateFinInst,
  maxDate,
  setModel,
  nominal,
  setNominal,
}) => {
  const { t } = useTranslation();
  const { data } = extProductsStore;
  const [prevType, setPrevType] = useState<ExtProductType | undefined>(undefined);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [linkedAccount, setLinkedAccount] = useState('');
  const { values } = formikProps;
  const minDate = useMemo(() => getYearsAndMonthsFromLuxon(DateTime.local().plus({ months: 1 })), []);
  const minDatePast = useMemo(() => getYearsAndMonthsFromLuxon(DateTime.local().minus({ years: 50 })), []);

  useEffect(() => {
    if (data?.type) setPrevType(data.type);
  }, [data?.type]);

  const setDefaultComposition = async (formikProps: FormikProps<ExternalProductData>) => {
    const { values } = formikProps;
    if (values.type === ExtProductType.ACCOUNT) {
      await formikProps.setFieldValue(`asset.CASH`, 100);
    } else if (values.type === ExtProductType.REAL_ESTATE) {
      await formikProps.setFieldValue(`asset.REAL_ESTATE`, 100);
    } else if (values.type === ExtProductType.REAL_ESTATE_RENT) {
      await formikProps.setFieldValue(`asset.REAL_ESTATE`, 100);
    } else if (values.type === ExtProductType.VALUABLES) {
      await formikProps.setFieldValue(`asset.COMMODITY`, 100);
    } else {
      await formikProps.setFieldValue(`asset.${ExtProductAsset.UNKNOWN}`, 100);
    }
  };

  return (
    <>
      <ConfirmDialog
        title={t('externalProduct_linkInternalAccount_title')}
        isOpen={accountsOpen}
        confirm={async () => {
          const ok = await extProductsStore.linkAccount(formikProps.values, linkedAccount, currency, values.id);
          if (ok) {
            setAccountsOpen(false);
            await formikProps.setFieldValue(`category`, ExtProductCategory.EDWARD);
          }
        }}
        close={() => {
          setAccountsOpen(false);
        }}
        btnCloseText={'general_cancel'}
        btnConfirmText={'general_use'}
        icons
      >
        {!extProductsStore.accounts.length ? (
          <Typography variant="h5">{t('externalProduct_linkInternalAccount_noAccounts')}</Typography>
        ) : (
          <TextField
            select={true}
            defaultValue={''}
            onChange={(e) => setLinkedAccount(e.target.value)} //store.switchAccount(e.target.value)
            label={t('externalProduct_linkInternalAccount_label')}
          >
            {extProductsStore.accounts.map((account) => (
              <MenuItem key={account.accountId} value={account.accountId}>
                {`${account.title} (VS: ${account.variableSymbol})`}
              </MenuItem>
            ))}
          </TextField>
        )}
      </ConfirmDialog>
      <Grid container alignItems="center">
        {[ExtProductCategory.EDWARD, ExtProductCategory.FINANCIAL_PRODUCT, ExtProductCategory.LOAN].includes(
          values.category,
        ) && (
          <>
            <Grid item xs={12}>
              <FinInstSearch
                store={extProductsStore}
                update={updateFinInst}
                fi={data?.financialInstitution || values.financialInstitution}
                errors={validationErrToForm(formikProps.errors.financialInstitution)}
              />
            </Grid>
            {values.financialInstitution?.internal && (
              <Grid item xs={12} style={{ display: 'flex' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    await extProductsStore.getAccounts(values.id);
                    setAccountsOpen(true);
                  }}
                  startIcon={<ConnectIcon transparentBgr />}
                >
                  {t('externalProduct_linkInternalAccount')}
                </Button>
                {values.accountId && (
                  <span
                    style={{
                      color: values.invalidLink ? EdwardColors.ALERT : EdwardColors.GREEN,
                      fontSize: '19px',
                      display: 'inline-flex',
                      alignSelf: 'center',
                      marginLeft: '8px',
                    }}
                  >
                    {values.invalidLink ? (
                      <CancelIcon style={{ marginRight: '4px' }} />
                    ) : (
                      <CheckCircleIcon style={{ marginRight: '4px' }} />
                    )}{' '}
                    {t(`externalProduct_account_${values.invalidLink ? 'invalid' : 'linked'}`)}
                  </span>
                )}
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormTextField name="title" label={t('externalProduct_title')} disabled={!!values.accountId} />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <FormSelectField
            name="type"
            label={t(`externalProduct_type${values.category === ExtProductCategory.PROPERTY ? '_property' : ''}`)}
            options={options.type[values.category].map((type) => ({
              value: type,
              label: t(`externalProduct_type_${type}`),
            }))}
            fast={false}
            disabled={!!values.accountId}
            beforeChange={() => setPrevType(values.type)}
            afterChange={async () => {
              extProductsStore.setLoading(true);
              await formikProps.setFieldValue(`interest`, f2p(options.interest[values.type]));

              const prevOptions = prevType ? options.asset[prevType].reduce((k, prev) => k + prev, '') : '';
              const currentOptions = prevType ? options.asset[values.type].reduce((k, prev) => k + prev, '') : '';
              if (prevType && prevOptions !== currentOptions) {
                options.asset[prevType].forEach(async (key) => {
                  !options.asset[values.type].includes(key) &&
                    (await formikProps.setFieldValue(`asset.${key}`, undefined));
                });
                options.asset[values.type].forEach(async (key) => {
                  await formikProps.setFieldValue(`asset.${key}`, 0);
                });
                await setDefaultComposition(formikProps);
              }

              if (!options.frequency[values.type].includes(values.frequency)) {
                const newFreq = options.frequency[values.type].includes(ExtProductFreq.MONTHLY)
                  ? ExtProductFreq.MONTHLY
                  : options.frequency[values.type].includes(ExtProductFreq.SINGLE)
                  ? ExtProductFreq.SINGLE
                  : '';
                await formikProps.setFieldValue('frequency', newFreq);
              }

              setModel(true);
              extProductsStore.setLoading(false);
              await formikProps.submitForm();
              setPrevType(values.type);
            }}
          />
        </Grid>

        {values.category === ExtProductCategory.PROPERTY && (
          <Grid item xs={12} sm={6}>
            <FormTextField name="title" label={t('externalProduct_title_property')} />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormNumberField
            name="balance.value"
            label={t(
              `externalProduct_balance${values.category === ExtProductCategory.PROPERTY ? '_property' : ''}${
                values.category === ExtProductCategory.LOAN ? '_loan' : ''
              }`,
            )}
            endAdornment={t(`currency_${currency}`)}
            disabled={!!values.accountId}
            fast={false}
            tooltipContent={t(
              `externalProduct_balance_tooltip${values.category === ExtProductCategory.PROPERTY ? '_property' : ''}${
                values.category === ExtProductCategory.LOAN ? '_loan' : ''
              }`,
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          {values.accountId ? (
            <TextField
              defaultValue={t('externalProduct_lastUpdate_daily')}
              label={t('externalProduct_lastUpdate')}
              disabled
            />
          ) : (
            <DatePicker
              maxDate={DateTime.local()}
              minDate={DateTime.local().minus({ years: 5 })}
              name="lastUpdate"
              label={t(
                `externalProduct_lastUpdate${values.category === ExtProductCategory.PROPERTY ? '_property' : ''}${
                  values.category === ExtProductCategory.LOAN ? '_loan' : ''
                }`,
              )}
            />
          )}
        </Grid>

        {[ExtProductCategory.EDWARD, ExtProductCategory.FINANCIAL_PRODUCT].includes(values.category) && (
          <>
            <Grid item xs={12} sm={6}>
              <FormNumberField
                name="periodic.value.value"
                label={t('externalProduct_periodic')}
                endAdornment={t(`currency_${currency}`)}
                afterBlur={async () => await extProductsStore.model(values)}
                disabled={!!values.accountId}
                tooltipContent={t('externalProduct_periodic_tooltip')}
              />
            </Grid>
            <Grid item xs={1}>
              {t(`general_Until`)}
            </Grid>
            <Grid item xs={11} sm={5}>
              <FormDateSelects
                name={`periodic.until`}
                minDate={minDate}
                maxDate={maxDate}
                fast={false}
                afterChange={async () => {
                  await extProductsStore.model(values);
                }}
                disabled={!!values.accountId}
              />
            </Grid>
          </>
        )}

        {[ExtProductType.RETIREMENT, ExtProductType.LTIP].includes(values.type) && (
          <>
            <Grid item xs={12} sm={6}>
              <FormNumberField
                name="employer.value.value"
                label={t('externalProduct_employer')}
                endAdornment={t(`currency_${currency}`)}
                disabled={!!values.accountId}
              />
            </Grid>
            <Grid item xs={1}>
              {t(`general_Until`)}
            </Grid>
            <Grid item xs={11} sm={5}>
              <FormDateSelects
                name={`employer.until`}
                fast={false}
                minDate={minDate}
                maxDate={maxDate}
                disabled={!!values.accountId}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormNumberField
                name="taxDeduction.value"
                label={t('externalProduct_taxDeduction')}
                endAdornment={t(`currency_${currency}`)}
                disabled={!!values.accountId}
                tooltipContent={t('externalProduct_taxDeduction_tooltip')}
              />
            </Grid>
            <Grid item xs={6} />
          </>
        )}

        {[ExtProductType.RETIREMENT, ExtProductType.BUILDING].includes(values.type) && (
          <>
            <Grid item xs={12} sm={6}>
              <FormNumberField
                name="government.value.value"
                label={t('externalProduct_government')}
                endAdornment={t(`currency_${currency}`)}
                disabled={!!values.accountId}
              />
            </Grid>
            <Grid item xs={1}>
              {t(`general_Until`)}
            </Grid>
            <Grid item xs={11} sm={5}>
              <FormDateSelects
                name={`government.until`}
                fast={false}
                minDate={minDate}
                maxDate={maxDate}
                disabled={!!values.accountId}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <FormNumberField
            name="interest"
            label={t(`externalProduct_interest${values.category === ExtProductCategory.LOAN ? '_loan' : ''}`)}
            endAdornment={'%'}
            decimalScale={2}
            disabled={values.invalidLink}
            fast={false}
            tooltipContent={
              values.category === ExtProductCategory.LOAN ? undefined : t('externalProduct_interest_tooltip')
            }
          />
        </Grid>
        <Grid item xs={6} />

        {([ExtProductType.REAL_ESTATE_RENT, ExtProductType.COMPANY, ExtProductType.OTHER_PROPERTY].includes(
          values.type,
        ) ||
          values.category === ExtProductCategory.LOAN) && (
          <>
            {values.category === ExtProductCategory.PROPERTY && (
              <>
                <Grid item xs={12} sm={6}>
                  <Switch
                    name={`usePeriodic`}
                    label={t('externalProduct_usePeriodic')}
                    tooltipContent={t('externalProduct_usePeriodic_tooltip')}
                  />
                </Grid>
                <Grid item xs={12} sm={6} />
              </>
            )}

            {(values.category === ExtProductCategory.LOAN || values.usePeriodic) && (
              <>
                {values.category !== ExtProductCategory.LOAN && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormNumberField
                        name="periodic.value.value"
                        label={t(
                          `externalProduct_periodic${
                            values.category === ExtProductCategory.PROPERTY ? '_property' : ''
                          }`,
                        )}
                        endAdornment={t(`currency_${currency}`)}
                        afterBlur={async () => await extProductsStore.model(values)}
                        disabled={extProductsStore.calculatingLoan}
                        fast={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} />
                  </>
                )}

                {values.category === ExtProductCategory.PROPERTY && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormSelectField
                        name="periodic.frequency"
                        label={t('externalProduct_freq')}
                        options={[DepositFrequency.MONTHLY, DepositFrequency.YEARLY].map((freq) => ({
                          value: freq,
                          label: t(`externalProduct_freq_${freq}`),
                        }))}
                        fast={false}
                        afterChange={async () => extProductsStore.model(values)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} />
                  </>
                )}

                <Grid item xs={12} sm={6} md={3}>
                  <GridSubtitle noMargin>
                    {t(values.category === ExtProductCategory.LOAN ? 'externalProduct_loan_since' : 'general_Since')}:
                  </GridSubtitle>
                  <FormDateSelects name={`periodic.since`} minDate={minDatePast} maxDate={maxDate} fast={false} />
                </Grid>

                {((values.periodic?.frequency &&
                  [DepositFrequency.MONTHLY, DepositFrequency.YEARLY, DepositFrequency.REPEAT_EVERY].includes(
                    values.periodic.frequency,
                  )) ||
                  values.category === ExtProductCategory.LOAN ||
                  values.category === ExtProductCategory.PROPERTY) && (
                  <Grid item xs={12} sm={6} md={3}>
                    <GridSubtitle noMargin>
                      {t(values.category === ExtProductCategory.LOAN ? 'externalProduct_loan_until' : 'general_Until')}:
                    </GridSubtitle>
                    <FormDateSelects
                      name={'periodic.until'}
                      minDate={minDate}
                      maxDate={maxDate}
                      fast={false}
                      afterChange={async () => {
                        setModel(true);
                        await formikProps.submitForm();
                      }}
                    />
                  </Grid>
                )}

                {values.category === ExtProductCategory.LOAN && (
                  <>
                    <Grid item xs={12} sm={6} />
                    <Grid item xs={12} sm={6}>
                      <FormNumberField
                        name="periodic.value.value"
                        label={t(`externalProduct_periodic_loan`)}
                        endAdornment={t(`currency_${currency}`)}
                        afterBlur={async () => {
                          await extProductsStore.model(values);
                          //!valueFilledIn(formikProps.values.value.value) &&
                            formikProps.setFieldValue('value.value', formikProps.values.periodic?.value?.value);
                        }}
                        disabled={extProductsStore.calculatingLoan}
                        fast={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {values.category === ExtProductCategory.LOAN && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={async () => {
                            await extProductsStore.calculateLoan(values, formikProps);
                          }}
                          startIcon={
                            extProductsStore.calculatingLoan ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <CalcIcon fontSize="medium" transparentBgr />
                            )
                          }
                          disabled={
                            extProductsStore.calculatingLoan ||
                            !valueFilledIn(values.balance.value) ||
                            !valueFilledIn(values.interest) ||
                            !valueFilledIn(values.periodic?.since) ||
                            !valueFilledIn(values.periodic?.until)
                          }
                        >
                          {t('externalProduct_calculateValue')}
                          <Info content={t('externalProduct_calculateLoan_tooltip')} button />
                        </Button>
                      )}
                    </Grid>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Grid>
      {values.category !== ExtProductCategory.LOAN && (
        <ExternalProductModalComposition
          productId={productId}
          options={options}
          formikProps={formikProps}
          currency={currency}
          setDefaultComposition={setDefaultComposition}
          store={extProductsStore}
          nominal={nominal}
          setNominal={setNominal}
        />
      )}
    </>
  );
};

export default compose(observer)(ExternalProductModalSetup);
